<template>
    <div>
        <div v-if="product.main_image" class="gallery d-flex flex-wrap"
             :class="{'is-modal': isModal}">
            <div
                v-if="otherImagesCount && $vuetify.display.lgAndUp"
                class="gallery-thumbs mt-2 mr-md-2 order-1 w-100"
            >
                <swiper
                    :modules="[Thumbs, EffectFade]"
                    :space-between="8"
                    :zoom="false"
                    watch-slides-progress
                    :slides-per-view="4.75"
                    @swiper="swiperThumbs = $event; emit('swiperThumbs', $event)"
                    @beforeDestroy="emit('swiperThumbs', null)"
                >
                    <swiper-slide class="pb-1">
                        <v-img
                            aspect-ratio="1"
                            :src="product.main_image?.url"
                            :alt="product.main_image?.alt"
                            class="rounded-normal bg-white"
                        ></v-img>
                    </swiper-slide>
                    <swiper-slide v-for="(media, index) in product.other_images" :key="index" class="pb-1">
                        <v-img
                            aspect-ratio="1"
                            :src="media.url"
                            :alt="media.alt"
                            class="rounded-normal bg-white"
                        ></v-img>
                    </swiper-slide>
                </swiper>
            </div>
            <div class="d-flex product-swiper align-center position-relative w-100">
                <div v-if="$vuetify.display.mdAndDown" class="swiper-button-prev mr-4" id="swiper-prev-product">
                    <v-icon :icon="mdiArrowLeft" color="grey-darken-1"></v-icon>
                </div>
                <swiper
                    v-if="$vuetify.display.lgAndUp && (swiperThumbs || !otherImagesCount)"
                    id="product-main-swiper"
                    :modules="[Thumbs, EffectFade, Navigation, Pagination]"
                    :thumbs="{ swiper: (props.otherImagesCount ? swiperThumbs : null), multipleActiveThumbs: false, autoScrollOffset: 1 }"
                    effect="fade"
                    :zoom="false"
                    class="flex-fill"
                    :navigation="{
                            nextEl: '#swiper-next-product',
                            prevEl: '#swiper-prev-product',
                        }"
                    :pagination="$vuetify.display.mdAndDown ? {clickable: true} : false"
                    @swiper="swiperMain = $event; emit('swiperMain', $event)"
                >
                    <tag :flags="product.flags"/>
                    <swiper-slide
                        class="d-flex swiper-no-swiping">
                        <v-img aspect-ratio="1"
                               :src="product.main_image?.url" width="600"
                               :alt="product.main_image?.alt"
                               class="flex-fill main-image bg-white cursor-pointer"
                               @click="emit('openGallery')"
                        >
                        </v-img>
                    </swiper-slide>
                    <swiper-slide v-for="media in product.other_images" :key="media.id"
                                  class="d-flex swiper-no-swiping">
                        <v-img aspect-ratio="1"
                               :src="media.url" width="600"
                               :alt="media.alt"
                               class="flex-fill main-image bg-white"
                               @click="emit('openGallery')">
                        </v-img>
                    </swiper-slide>
                </swiper>
                <swiper
                    v-if="$vuetify.display.mdAndDown"
                    id="product-main-swiper-mobile"
                    :modules="[EffectFade, Navigation, Pagination]"
                    effect="fade"
                    class="flex-fill"
                    :zoom="false"
                    :no-swiping="false"
                    :navigation="{
                            nextEl: '#swiper-next-product',
                            prevEl: '#swiper-prev-product',
                        }"
                    :pagination="{clickable: true}"
                    @swiper="emit('swiperMain', $event)"
                >
                    <tag :flags="product.flags"/>
                    <swiper-slide
                        class="d-flex swiper-no-swiping">
                        <v-img aspect-ratio="1"
                               :src="product.main_image?.url" width="600"
                               :alt="product.main_image?.alt"
                               class="flex-fill main-image bg-white cursor-pointer"
                               @click="emit('openGallery')"
                        >
                        </v-img>
                    </swiper-slide>
                    <swiper-slide v-for="media in product.other_images" :key="media.id"
                                  class="d-flex swiper-no-swiping">
                        <v-img aspect-ratio="1"
                               :src="media.url" width="600"
                               :alt="media.alt"
                               class="flex-fill main-image bg-white"
                               @click="emit('openGallery')">
                        </v-img>
                    </swiper-slide>
                </swiper>
                <div v-if="$vuetify.display.mdAndDown" class="swiper-button-next ml-4" id="swiper-next-product">
                    <v-icon :icon="mdiArrowRight" color="grey-darken-1"></v-icon>
                </div>
            </div>
        </div>
        <div v-else class="position-relative h-100" style="z-index: 1;">
            <tag :flags="product.flags"/>
        </div>
    </div>
</template>


<script setup>
import {EffectFade, Navigation, Pagination, Thumbs} from "swiper/modules";
import {mdiArrowLeft, mdiArrowRight} from "@mdi/js";
import Tag from "./Tag.vue";
import {Swiper, SwiperSlide} from "swiper/vue";
import 'swiper/css';
import "swiper/css/thumbs";
import "swiper/css/effect-fade";
import 'swiper/css/pagination';
import { ref } from 'vue';

const props = defineProps({
    product: Object,
    isModal: {
        type: Boolean,
        default: true
    },
    otherImagesCount: Number
})

const swiperThumbs = ref(null);
const swiperMain = ref(null);

const emit = defineEmits(['openGallery', 'swiperMain', 'swiperThumbs']);
</script>
<style scoped lang="scss">
@use '../../../../plugins/vuetify-settings' as *;
@use 'vuetify/lib/styles/settings/variables' as *;
@use 'sass:map';
.gallery {
    &.is-modal {
        flex-wrap: wrap !important;

        div.gallery-thumbs {
            order: 1 !important;
            margin-top: map.get($spacers, 2) !important;
        }
    }
}

#main-image {
    flex: 1 1 auto;
}

.gallery-thumbs {
    flex: 1 1 100%;

    .swiper-vertical {
        margin-bottom: -11px;
    }
}

:deep(#product-main-swiper){
    .swiper-pagination-bullet-active{
        background: map.get($grey, darken-1);
        border-radius: 0 !important;
    }
    .swiper-pagination-bullet{
        width: 16px !important;
        border-radius: 0 !important;
        height: 4px;
    }
}
</style>
